<template>
  <InfoBox :items="items" class="route-summary" />
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { ZonedDateTimeRange } from '@rygen/rygen-ui/model';

import { Mileages } from '@/composables/routeBuilder';
import { ProposedShipment } from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';

import InfoBox, { InfoBoxItem } from '@/components/ui/InfoBox.vue';

const NUM_MILES_PER_DAY = 400;

const props = defineProps({
  proposedShipments: {
    type: Array as PropType<ProposedShipment[] | RoutePlanProposedShipment[]>,
    default: () => [],
  },
  mileages: {
    type: Object as PropType<Mileages>,
    default: () => ({}),
  },
});
const { proposedShipments, mileages } = toRefs(props);

const { n, t } = useI18n();

const totalMileage = computed(() => {
  return Object.values(mileages.value).reduce((acc, curr) => {
    const stopResponses = curr?.response.data ?? [];
    const totalMileageForShipment = stopResponses.reduce(
      (shipmentMileage, stopResponse) => {
        const stopMileage = stopResponse?.mileage ?? 0;
        return shipmentMileage + stopMileage;
      },
      0,
    );
    return acc + totalMileageForShipment;
  }, 0);
});

const formattedMileage = computed(() => {
  // Don't show 0; if there are no shipments, show placeholder
  return totalMileage.value
    ? t('common.paramMilesAbbr', {
        param: n(totalMileage.value, { maximumFractionDigits: 1 }),
      })
    : '-';
});

const isAnyMileageLoading = computed(() => {
  return Object.values(mileages.value).some((m) => m?.response.loading);
});

const requestedApptPerShipment = computed(() => {
  return proposedShipments.value.map((s) => {
    const earliestPickupAppt =
      s.origin?.requestedAppointmentEarliest ??
      s.origin?.requestedAppointmentLatest;
    const latestDropoffAppt =
      s.destination?.requestedAppointmentLatest ??
      s.destination?.requestedAppointmentEarliest;
    return !!earliestPickupAppt && !!latestDropoffAppt
      ? new ZonedDateTimeRange({
          start: earliestPickupAppt,
          end: latestDropoffAppt,
        })
      : undefined;
  });
});

const transitTimePerShipment = computed(() => {
  return Object.values(mileages.value)
    .map((m) => m?.response.data ?? [])
    .map((mileageResponse, index) => {
      const requestedAppt = requestedApptPerShipment.value[index];
      if (requestedAppt) {
        // `requestedApptPerShipment` only creates the range if both dates are
        // available
        return requestedAppt.end!.diff(requestedAppt.start!).as('days');
      } else {
        const totalMileage = mileageResponse
          .map((res) => res.mileage ?? 0)
          .reduce((total, curr) => total + curr, 0);
        return totalMileage / NUM_MILES_PER_DAY;
      }
    });
});

const totalOverlapDays = computed(() => {
  return requestedApptPerShipment.value.reduce(
    (totalOverlap, currAppt, currIndex, allAppts) => {
      if (!currAppt) {
        return totalOverlap;
      }

      const overlap = allAppts
        .filter((appt, i) => i > currIndex && currAppt.isOverlappedBy(appt))
        .map((appt) => currAppt.intersectionWith(appt))
        .map(({ start, end }) => {
          return !!start && !!end ? end.diff(start).as('days') : 0;
        })
        .reduce((acc, curr) => acc + curr, 0);

      return totalOverlap + overlap;
    },
    0,
  );
});

const totalTransitTime = computed(() => {
  const naiveTotal = transitTimePerShipment.value.reduce(
    (total, curr) => total + curr,
    0,
  );

  // `naiveTotal` is the sum of individual shipments' transit times, but some
  // shipments could overlap. Find any overlap and subtract it from the total.
  return naiveTotal - totalOverlapDays.value;
});

const formattedTransitTime = computed(() => {
  const numDays = totalTransitTime.value;
  return numDays ? t('rateShop.countDays', { count: numDays.toFixed(2) }) : '-';
});

const items: Ref<InfoBoxItem[]> = computed(() => [
  {
    id: 'distance',
    label: t('common.totalParam', { param: t('common.distances') }),
    value: formattedMileage.value,
    loading: isAnyMileageLoading.value,
  },
  {
    id: 'transit',
    label: t('common.estimatedParam', { param: t('shipments.transitTime') }),
    value: formattedTransitTime.value,
    loading: isAnyMileageLoading.value,
  },
  {
    id: 'shipments',
    label: t('common.totalParam', { param: t('shipments.shipments', 2) }),
    value: proposedShipments.value.length || '-',
  },
]);
</script>
